import { useCallback, useEffect, useRef, useState } from "react";
import { updateMindMapWriting, updateWriting } from "../../api/commands.js";
import { useAutosave } from "../../hooks/useAutosave.js";
import type { NodeWithWriting } from "./MapEditingContext.js";
import { ColorDot, useT } from "@mmm/ui";
const WRITING_PANEL_HEIGHT_KEY = "mmm-writing-panel-height";
const DEFAULT_WRITING_PANEL_HEIGHT = 320;
const MIN_WRITING_PANEL_HEIGHT = 240;
const MAX_WRITING_PANEL_HEIGHT = 900;

function clampWritingPanelHeight(height: number) {
  return Math.min(MAX_WRITING_PANEL_HEIGHT, Math.max(MIN_WRITING_PANEL_HEIGHT, Math.round(height)));
}

function readWritingPanelHeight() {
  const stored = Number(localStorage.getItem(WRITING_PANEL_HEIGHT_KEY));
  return Number.isFinite(stored) && stored > 0 ? clampWritingPanelHeight(stored) : DEFAULT_WRITING_PANEL_HEIGHT;
}

/**
 * A full-width writing card below the map. The selected node's notes appear
 * while a node is selected; otherwise it shows notes for the whole map's
 * subject or chapter scope. Keyed by the active record so the draft is seeded
 * from saved content on every switch instead of syncing through an effect.
 */
export function WritingPanel({
  node,
  mindMapId,
  mindMapMarkdown,
  isChapter,
}: {
  node: NodeWithWriting | null;
  mindMapId: string;
  mindMapMarkdown: string;
  isChapter: boolean;
}) {
  const t = useT();
  const [markdown, setMarkdown] = useState(node?.writing.markdown ?? mindMapMarkdown);
  const [textareaHeight, setTextareaHeight] = useState(readWritingPanelHeight);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { saveNow } = useAutosave(
    markdown,
    (next) => {
      return node
        ? updateWriting({ nodeId: node.id, markdown: next })
        : updateMindMapWriting({ mindMapId, markdown: next });
    },
    { key: node ? `writing:${node.id}` : `map-writing:${mindMapId}` }
  );

  const persistTextareaHeight = useCallback((height: number) => {
    const next = clampWritingPanelHeight(height);
    setTextareaHeight((current) => (current === next ? current : next));
    localStorage.setItem(WRITING_PANEL_HEIGHT_KEY, String(next));
  }, []);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || typeof ResizeObserver === "undefined") return;

    let frame = 0;
    const observer = new ResizeObserver(() => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => persistTextareaHeight(textarea.offsetHeight));
    });

    observer.observe(textarea);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [persistTextareaHeight]);

  return (
    <div className="mmm-card mmm-writing-panel">
      <div className="mmm-writing-panel-head">
        {node && <ColorDot color={node.color} size={10} />}
        <span className="mmm-writing-panel-title">
          {node ? node.title : t(isChapter ? "maps.chapterWriting" : "maps.subjectWriting")}
        </span>
      </div>
      <textarea
        ref={textareaRef}
        className="mmm-textarea mmm-writing-textarea"
        style={{ height: textareaHeight }}
        aria-label={node ? node.title : t(isChapter ? "maps.chapterWriting" : "maps.subjectWriting")}
        placeholder={node
          ? t("maps.writingPlaceholder")
          : t(isChapter ? "maps.chapterWritingPlaceholder" : "maps.subjectWritingPlaceholder")}
        value={markdown}
        onChange={(event) => setMarkdown(event.target.value)}
        onBlur={saveNow}
      />
    </div>
  );
}
